import React, { useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertCircle, Eye, EyeOff, Lock, Check, X, CheckCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { api } from '../../services/api';
import { useTranslation } from 'react-i18next';
import AuthLayout from '../../layouts/AuthLayout';

const ResetPassword: React.FC = () => {
  const { t } = useTranslation('auth');
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const email = searchParams.get('email') || '';

  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [success, setSuccess] = useState(false);

  const passwordRules = useMemo(() => {
    return {
      min: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      special: /[^A-Za-z0-9]/.test(password),
    };
  }, [password]);

  const allRulesMet = passwordRules.min && passwordRules.uppercase && passwordRules.lowercase && passwordRules.special;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setValidationErrors({});

    const errors: Record<string, string> = {};
    if (!allRulesMet) {
      errors.password = t('register.password_rule_min');
    }
    if (password !== passwordConfirmation) {
      errors.password_confirmation = t('register.password_mismatch');
    }
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    setLoading(true);
    try {
      await api.post('/reset-password', {
        email,
        token,
        password,
        password_confirmation: passwordConfirmation,
      });
      setSuccess(true);
    } catch (err: any) {
      if (err.errors) {
        setValidationErrors(
          Object.fromEntries(
            Object.entries(err.errors).map(([key, val]) => [key, Array.isArray(val) ? val[0] : val])
          ) as Record<string, string>
        );
      } else {
        setError(err.message || t('reset_password.error_generic'));
      }
    } finally {
      setLoading(false);
    }
  };

  if (!token || !email) {
    return (
      <AuthLayout>
        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border dark:border-slate-700 p-6 sm:p-8 text-center">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/30 flex items-center justify-center">
              <X className="h-8 w-8 text-red-600" />
            </div>
          </div>
          <h2 className="text-2xl font-bold mb-2">{t('reset_password.error_invalid_link')}</h2>
          <Link to="/login" className="mt-4 inline-block">
            <Button className="w-full">{t('reset_password.signin_button')}</Button>
          </Link>
        </div>
      </AuthLayout>
    );
  }

  if (success) {
    return (
      <AuthLayout>
        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border dark:border-slate-700 p-6 sm:p-8 text-center">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center">
              <CheckCircle className="h-8 w-8 text-emerald-600" />
            </div>
          </div>
          <h2 className="text-2xl font-bold mb-2">{t('reset_password.success_title')}</h2>
          <p className="text-sm text-muted-foreground mb-6">{t('reset_password.success_message')}</p>
          <Link to="/login">
            <Button className="w-full">{t('reset_password.signin_button')}</Button>
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border dark:border-slate-700 p-6 sm:p-8">
        <h2 className="text-2xl font-bold text-center mb-1">{t('reset_password.title')}</h2>
        <p className="text-sm text-muted-foreground text-center mb-6">{t('reset_password.subtitle')}</p>

        <div className="space-y-4">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300 p-3 rounded-md text-sm flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="relative">
              <div className="absolute left-3 top-3 text-muted-foreground">
                <Lock className="h-4 w-4" />
              </div>
              <Input
                type={showPassword ? 'text' : 'password'}
                placeholder={t('reset_password.new_password')}
                value={password}
                onChange={(e) => { setPassword(e.target.value); setValidationErrors({}); setError(null); }}
                className={`pl-10 ${validationErrors.password ? 'border-red-500' : ''}`}
                disabled={loading}
              />
              <div
                className="absolute right-3 top-3 text-muted-foreground cursor-pointer"
                tabIndex={-1}
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </div>
              {validationErrors.password && (
                <div className="text-red-500 text-xs mt-1 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {validationErrors.password}
                </div>
              )}
            </div>

            {password.length > 0 && (
              <div className="space-y-1 text-xs">
                {Object.entries(passwordRules).map(([key, met]) => (
                  <div key={key} className={`flex items-center gap-1.5 ${met ? 'text-emerald-600' : 'text-muted-foreground'}`}>
                    {met ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                    {t(`register.password_rule_${key}`)}
                  </div>
                ))}
              </div>
            )}

            <div className="relative">
              <div className="absolute left-3 top-3 text-muted-foreground">
                <Lock className="h-4 w-4" />
              </div>
              <Input
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder={t('reset_password.confirm_password')}
                value={passwordConfirmation}
                onChange={(e) => { setPasswordConfirmation(e.target.value); setValidationErrors({}); }}
                className={`pl-10 ${validationErrors.password_confirmation ? 'border-red-500' : ''}`}
                disabled={loading}
              />
              <div
                className="absolute right-3 top-3 text-muted-foreground cursor-pointer"
                tabIndex={-1}
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </div>
              {validationErrors.password_confirmation && (
                <div className="text-red-500 text-xs mt-1 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {validationErrors.password_confirmation}
                </div>
              )}
            </div>

            <Button type="submit" className="w-full" disabled={loading || !allRulesMet}>
              {loading ? t('reset_password.resetting') : t('reset_password.submit_button')}
            </Button>
          </form>
        </div>
      </div>
    </AuthLayout>
  );
};

export default ResetPassword;
